import { Injectable, inject } from '@angular/core';
import { distinctUntilChanged, filter, map, withLatestFrom } from 'rxjs';
import { HeatloadStore } from '../../state/heatload/heatload.store';
import { ProjectStore } from '../../state/project/project.store';
import { FormName, ProjectType } from '@nx-customer-apps/shared/enums';
import { Modernization } from '@nx-customer-apps/shared/interfaces';
import { HeatloadFormOptionsService } from './heatload-form-options.service';
import { HeatloadPrefillMapper, ProjectsService } from '../../services';
import { AccordionService } from '../../shared/interfaces';
import { ModernizationsFormService } from '../../shared/components/modernizations';

@Injectable({
    providedIn: 'root'
})
export class ModernizationsService implements AccordionService {
    private heatloadStore = inject(HeatloadStore);
    private prefillMapper = inject(HeatloadPrefillMapper);
    private heatloadFormOptionsService = inject(HeatloadFormOptionsService);
    private projectStore = inject(ProjectStore);
    private projectsService = inject(ProjectsService);
    private formService = inject(ModernizationsFormService);

    public questions$ = this.heatloadStore.livingArea$.pipe(
        withLatestFrom(this.projectStore.project$, this.heatloadStore.cache$, (livingArea, project, cache) => ({
            livingArea,
            project,
            cache
        })),
        filter(({ project }) => this.projectsService.isRenovation(project!.projectType as ProjectType)),
        distinctUntilChanged((prev, curr) => prev.livingArea === curr.livingArea),
        map(state => {
            if (!state.livingArea) {
                return null;
            }
            const projectPrefill = this.prefillMapper.projectToModernizationPrefill(state.project!);
            const cachePrefill = this.prefillMapper.stateToModernizationPrefill(state.cache?.modernization);
            const formOptions = this.heatloadFormOptionsService.getModernizationFormOptions(cachePrefill || projectPrefill);
            return this.formService.getModernizationQuestions(formOptions);
        })
    );

    public getSuccessMessage(modernization: Modernization): string {
        const results = [
            modernization.buildingConstructionYear,
            modernization.roofModernization,
            modernization.wallsModernization,
            modernization.windowsModernization
        ];

        const message = results.filter(Boolean).join(', ');

        return message;
    }

    public isExpanded$ = this.heatloadStore.sameValueSubmited$.pipe(
        withLatestFrom(this.projectStore.project$, (form, project) => ({
            form,
            project
        })),
        map(
            ({ form, project }) =>
                form?.formName === FormName.LivingArea && this.projectsService.isRenovation(project!.projectType as ProjectType)
        )
    );
}
